<script setup>
/**
 * Fork overlay (new file, no OSS equivalent) — EVA-M6, extended in EVA-M7 (7.1).
 * The assistant settings page: lists the account's assistants as selectable cards and opens one in a
 * tabbed editor — Settings (AssistantSettingsForm), Inboxes (AssignedInboxesPanel — bind to inboxes),
 * Knowledge (DocumentsPanel), Scenarios (ScenariosPanel) and the account-level Custom tools
 * (CustomToolsPanel). The per-assistant tabs appear only once the assistant is saved (they need its
 * id) — a signpost tells the user to save first. The page header carries the account-level Eva
 * kill-switch (M7 A2) and the editor header carries delete behind a confirmation (M7 A3/B5). The
 * account-level SpendCard sits at the bottom so it never reads as part of one assistant. Persists
 * the form submit through EvaAssistantsAPI, surfacing backend validation errors (M7 D3).
 * Tailwind-only.
 */
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EvaAssistantsAPI from 'dashboard/api/eva/assistants';
import EvaAccountSettingsAPI from 'dashboard/api/eva/accountSettings';
import { evaErrorMessages } from 'dashboard/api/eva/errors';
import AssistantSettingsForm from 'dashboard/components-next/Eva/AssistantSettingsForm.vue';
import AssignedInboxesPanel from 'dashboard/components-next/Eva/AssignedInboxesPanel.vue';
import DocumentsPanel from 'dashboard/components-next/Eva/DocumentsPanel.vue';
import FaqApprovalPanel from 'dashboard/components-next/Eva/FaqApprovalPanel.vue';
import ScenariosPanel from 'dashboard/components-next/Eva/ScenariosPanel.vue';
import CustomToolsPanel from 'dashboard/components-next/Eva/CustomToolsPanel.vue';
import ModelAccessCard from 'dashboard/components-next/Eva/ModelAccessCard.vue';
import SpendCard from 'dashboard/components-next/Eva/SpendCard.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();

const assistants = ref([]);
const selected = ref(null);
const isSaving = ref(false);
const isDeleting = ref(false);
const activeTab = ref(0);
const status = ref(''); // '' | 'saved' | 'error'
const errorMessages = ref([]);
const evaEnabled = ref(true);
const confirmDialog = ref(null);
const assistantForm = ref(null);

// The account-level model narrowing changed → refresh the open assistant picker's /eva/models list.
const onModelsNarrowed = () => assistantForm.value?.loadModels();

const tabs = computed(() => [
  { key: 'settings', label: t('EVA_ASSISTANT_SETTINGS.TAB_SETTINGS') },
  { key: 'inboxes', label: t('EVA_INBOXES.TAB') },
  { key: 'documents', label: t('EVA_DOCUMENTS.TITLE') },
  { key: 'faqs', label: t('EVA_FAQ_APPROVAL.TAB') },
  { key: 'scenarios', label: t('EVA_SCENARIOS.TITLE') },
  { key: 'tools', label: t('EVA_CUSTOM_TOOLS.TITLE') },
]);

const loadAssistants = async () => {
  // Don't reset `status` here: save() sets 'saved' then awaits this reload, so
  // clearing would wipe the success message before it ever renders. edit()/
  // createNew() reset status on navigation; on mount it starts ''.
  try {
    const { data } = await EvaAssistantsAPI.get();
    assistants.value = data;
  } catch {
    status.value = 'error';
  }
};

const loadSettings = async () => {
  try {
    const { data } = await EvaAccountSettingsAPI.get();
    evaEnabled.value = data.eva_enabled;
  } catch {
    /* leave the toggle at its default-on; a failed read shouldn't block the page */
  }
};

const toggleEnabled = async value => {
  evaEnabled.value = value;
  try {
    await EvaAccountSettingsAPI.update({ eva_enabled: value });
  } catch {
    await loadSettings(); // resync to the true server state (robust to a racing double-toggle)
  }
};

const resetEditorState = () => {
  activeTab.value = 0;
  status.value = '';
  errorMessages.value = [];
};

const edit = assistant => {
  selected.value = assistant;
  resetEditorState();
};

const createNew = () => {
  selected.value = {};
  resetEditorState();
};

const onTabChanged = tab => {
  activeTab.value = tabs.value.findIndex(entry => entry.key === tab.key);
};

const save = async payload => {
  isSaving.value = true;
  status.value = '';
  errorMessages.value = [];
  try {
    const { data } = selected.value?.id
      ? await EvaAssistantsAPI.update(selected.value.id, payload)
      : await EvaAssistantsAPI.create(payload);
    selected.value = data;
    status.value = 'saved';
    await loadAssistants();
  } catch (error) {
    status.value = 'error';
    errorMessages.value = evaErrorMessages(error);
  } finally {
    isSaving.value = false;
  }
};

const requestDelete = () => confirmDialog.value?.open();

const confirmDelete = async () => {
  if (!selected.value?.id) return;
  isDeleting.value = true;
  try {
    await EvaAssistantsAPI.delete(selected.value.id);
    selected.value = null;
    await loadAssistants();
  } catch (error) {
    status.value = 'error';
    // Show the backend reason if any, else a delete-specific message (not the generic save error).
    const messages = evaErrorMessages(error);
    errorMessages.value = messages.length
      ? messages
      : [t('EVA_ASSISTANT_SETTINGS.DELETE_ERROR')];
  } finally {
    isDeleting.value = false;
    confirmDialog.value?.close();
  }
};

onMounted(() => {
  loadAssistants();
  loadSettings();
});

defineExpose({
  loadAssistants,
  loadSettings,
  toggleEnabled,
  edit,
  createNew,
  save,
  onTabChanged,
  requestDelete,
  confirmDelete,
});
</script>

<template>
  <div class="flex flex-col gap-5 min-w-0">
    <header class="flex flex-wrap items-center justify-between gap-3">
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ t('EVA_ASSISTANT_SETTINGS.PAGE_TITLE') }}
      </h2>
      <div class="flex items-center gap-4">
        <label class="flex items-center gap-2 text-sm text-n-slate-11">
          {{ t('EVA_ASSISTANT_SETTINGS.ENABLED') }}
          <Switch
            :model-value="evaEnabled"
            @update:model-value="toggleEnabled"
          />
        </label>
        <Button
          :label="t('EVA_ASSISTANT_SETTINGS.NEW')"
          sm
          @click="createNew"
        />
      </div>
    </header>

    <p
      v-if="!evaEnabled"
      class="flex items-start gap-2 px-3 py-2 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
    >
      <span class="i-lucide-pause-circle size-4 mt-0.5 shrink-0" />
      {{ t('EVA_ASSISTANT_SETTINGS.DISABLED_NOTICE') }}
    </p>

    <p
      v-if="status === 'saved'"
      class="flex items-start gap-2 px-3 py-2 text-sm rounded-lg bg-n-teal-3 text-n-teal-11"
    >
      <span class="i-lucide-check size-4 mt-0.5 shrink-0" />
      {{ t('EVA_ASSISTANT_SETTINGS.SAVED') }}
    </p>
    <p
      v-else-if="status === 'error'"
      class="flex items-start gap-2 px-3 py-2 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
    >
      <span class="i-lucide-triangle-alert size-4 mt-0.5 shrink-0" />
      {{
        errorMessages.length
          ? errorMessages.join(' ')
          : t('EVA_ASSISTANT_SETTINGS.SAVE_ERROR')
      }}
    </p>

    <ul
      v-if="assistants.length"
      class="grid grid-cols-1 gap-3 list-none sm:grid-cols-2 xl:grid-cols-3"
    >
      <li v-for="assistant in assistants" :key="assistant.id" class="min-w-0">
        <button
          class="flex items-center w-full gap-3 p-3 text-left transition-colors rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1"
          :class="
            selected && selected.id === assistant.id
              ? 'outline-n-iris-9'
              : 'outline-n-container hover:outline-n-slate-6'
          "
          @click="edit(assistant)"
        >
          <span
            class="flex items-center justify-center text-white rounded-lg size-9 shrink-0 bg-gradient-to-br from-n-iris-9 to-n-blue-9"
          >
            <span class="i-lucide-sparkles size-4" />
          </span>
          <span class="flex flex-col min-w-0">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ assistant.name }}
            </span>
            <span
              v-if="assistant.description"
              class="text-xs truncate text-n-slate-11"
            >
              {{ assistant.description }}
            </span>
          </span>
        </button>
      </li>
    </ul>
    <div
      v-else-if="!selected"
      class="flex flex-col items-center gap-3 px-6 py-10 text-center border border-dashed rounded-xl border-n-weak"
    >
      <span
        class="flex items-center justify-center text-white rounded-xl size-12 bg-gradient-to-br from-n-iris-9 to-n-blue-9"
      >
        <span class="i-lucide-sparkles size-6" />
      </span>
      <p class="max-w-sm text-sm text-n-slate-11">
        {{ t('EVA_ASSISTANT_SETTINGS.EMPTY_STATE') }}
      </p>
      <Button
        :label="t('EVA_ASSISTANT_SETTINGS.CREATE_FIRST')"
        @click="createNew"
      />
    </div>

    <template v-if="selected">
      <section class="flex flex-col gap-4 min-w-0">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-3 min-w-0">
            <span
              class="flex items-center justify-center text-white rounded-lg size-9 shrink-0 bg-gradient-to-br from-n-iris-9 to-n-blue-9"
            >
              <span class="i-lucide-sparkles size-4" />
            </span>
            <div class="flex flex-col min-w-0">
              <h3 class="text-base font-medium truncate text-n-slate-12">
                {{
                  selected.id ? selected.name : t('EVA_ASSISTANT_SETTINGS.NEW')
                }}
              </h3>
              <p
                v-if="selected.description"
                class="text-xs truncate text-n-slate-11"
              >
                {{ selected.description }}
              </p>
            </div>
          </div>
          <Button
            v-if="selected.id"
            ghost
            ruby
            sm
            icon="i-lucide-trash"
            :label="t('EVA_ASSISTANT_SETTINGS.DELETE')"
            class="shrink-0"
            @click="requestDelete"
          />
        </div>

        <TabBar
          v-if="selected.id"
          :tabs="tabs"
          :initial-active-tab="activeTab"
          @tab-changed="onTabChanged"
        />
        <p v-else class="flex items-start gap-2 text-sm text-n-slate-11">
          <span class="i-lucide-info size-4 mt-0.5 shrink-0" />
          {{ t('EVA_ASSISTANT_SETTINGS.SAVE_FIRST') }}
        </p>

        <AssistantSettingsForm
          v-if="activeTab === 0"
          ref="assistantForm"
          :assistant="selected"
          :is-saving="isSaving"
          @submit="save"
        />
        <AssignedInboxesPanel
          v-else-if="activeTab === 1 && selected.id"
          :assistant-id="selected.id"
        />
        <DocumentsPanel
          v-else-if="activeTab === 2 && selected.id"
          :assistant-id="selected.id"
        />
        <FaqApprovalPanel
          v-else-if="activeTab === 3 && selected.id"
          :assistant-id="selected.id"
        />
        <ScenariosPanel
          v-else-if="activeTab === 4 && selected.id"
          :assistant-id="selected.id"
        />
        <CustomToolsPanel v-else-if="activeTab === 5 && selected.id" />
      </section>
    </template>
    <p v-else-if="assistants.length" class="text-sm text-n-slate-11">
      {{ t('EVA_ASSISTANT_SETTINGS.PICK_OR_CREATE') }}
    </p>

    <ModelAccessCard @saved="onModelsNarrowed" />

    <SpendCard />

    <Dialog
      ref="confirmDialog"
      type="alert"
      :title="t('EVA_ASSISTANT_SETTINGS.DELETE_TITLE')"
      :description="t('EVA_ASSISTANT_SETTINGS.DELETE_DESCRIPTION')"
      :confirm-button-label="t('EVA_ASSISTANT_SETTINGS.DELETE')"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
    />
  </div>
</template>
